import React from 'react';
import { Helmet } from 'react-helmet-async';
import { ChefHat } from 'lucide-react';

export const EggFooYoung: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>Classic Egg Foo Young Recipe | Chinese Food Maps</title>
        <meta name="description" content="Learn how to make authentic Chinese egg foo young at home. Master the art of creating these savory egg patties with our detailed recipe and gravy instructions." />
        <meta property="og:title" content="Classic Egg Foo Young Recipe | Chinese Food Maps" />
        <meta property="og:description" content="Learn how to make authentic Chinese egg foo young at home." />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://chinesefoodmaps.com/recipes/egg-foo-young" />
        <meta property="og:image" content="https://cdn-icons-png.flaticon.com/512/2548/2548242.png" />
        <link rel="canonical" href="https://chinesefoodmaps.com/recipes/egg-foo-young" />
      </Helmet>
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-6 text-red-900">Classic Egg Foo Young Recipe</h1>
        
        <div className="bg-white rounded-lg shadow-md p-8">
          <div className="prose max-w-none text-gray-600">
            <p className="text-lg leading-relaxed mb-6">
              Egg Foo Young is a Chinese-style omelet filled with vegetables and meat, served with a rich brown gravy. This recipe will help you create this classic dish at home.
            </p>

            <h3 className="text-xl font-semibold text-red-800 mb-4">For the Egg Patties:</h3>
            <ul className="list-disc pl-6 mb-6 space-y-2">
              <li>8 large eggs</li>
              <li>1 cup bean sprouts</li>
              <li>½ cup chopped onion</li>
              <li>½ cup chopped celery</li>
              <li>½ cup chopped carrots</li>
              <li>1 cup cooked chicken or char siu pork, diced</li>
              <li>2 green onions, chopped</li>
              <li>1 tbsp soy sauce</li>
              <li>¼ tsp white pepper</li>
              <li>Oil for frying</li>
            </ul>

            <h3 className="text-xl font-semibold text-red-800 mb-4">For the Gravy:</h3>
            <ul className="list-disc pl-6 mb-6 space-y-2">
              <li>2 cups chicken stock</li>
              <li>2 tbsp oyster sauce</li>
              <li>1 tbsp soy sauce</li>
              <li>1 tsp sesame oil</li>
              <li>2 tbsp cornstarch mixed with ¼ cup water</li>
              <li>¼ tsp white pepper</li>
            </ul>

            <h3 className="text-xl font-semibold text-red-800 mb-4">Instructions:</h3>
            <ol className="list-decimal pl-6 mb-6 space-y-4">
              <li>
                <strong>Prepare Mixture:</strong> Beat eggs with soy sauce and pepper. Fold in vegetables and meat.
              </li>
              <li>
                <strong>Make Gravy:</strong> Combine stock, oyster sauce, and soy sauce. Simmer and thicken with cornstarch.
              </li>
              <li>
                <strong>Cook Patties:</strong> Heat oil in a pan. Pour ½ cup egg mixture for each patty.
              </li>
              <li>
                <strong>Flip:</strong> Cook until golden brown, about 2-3 minutes per side.
              </li>
              <li>
                <strong>Serve:</strong> Top with hot gravy and garnish with green onions.
              </li>
            </ol>

            <div className="bg-yellow-50 rounded-lg p-6 mt-8">
              <h4 className="text-lg font-semibold text-red-800 mb-2">Pro Tips from Chinese Food Maps:</h4>
              <ul className="list-disc pl-6 space-y-2">
                <li>Drain vegetables well to prevent watery patties</li>
                <li>Use medium-high heat for crispy edges</li>
                <li>Make gravy first and keep it warm</li>
                <li>Don't overcrowd the pan when frying</li>
                <li>Serve immediately while patties are crispy</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};